import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Heart, Trophy, Flame, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BuddyPublicPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/buddy/public/${token}`);
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Länken finns inte längre');
      } finally { if (!cancelled) setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#FFE8F5] via-[#F5EEFF] to-[#E8F0FF]">
        <Loader2 className="w-10 h-10 animate-spin opacity-60" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-[#FFE8F5] via-[#F5EEFF] to-[#E8F0FF]">
        <div className="neo-card p-6 bg-white max-w-sm text-center" data-testid="buddy-error">
          <p className="text-4xl mb-2">🌸</p>
          <h2 className="font-['Fredoka'] font-bold text-xl mb-2">Hoppsan</h2>
          <p className="text-sm opacity-70">{error}</p>
          <p className="text-xs opacity-50 mt-3">Länken kan ha återkallats av personen som delade den.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 sm:p-8 bg-gradient-to-br from-[#FFE8F5] via-[#F5EEFF] to-[#E8F0FF]" data-testid="buddy-public-page">
      <div className="max-w-md mx-auto space-y-4">
        <div className="text-center pt-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] opacity-60">Buddy-länk · skrivskyddad</p>
          <h1 className="font-['Fredoka'] font-bold text-3xl mt-1">Heja {data.display_name}!</h1>
          <p className="text-sm opacity-70 mt-1">Här ser du hur det går just nu 💛</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="neo-card p-4 bg-white text-center" data-testid="buddy-streak">
            <Flame className="w-7 h-7 mx-auto text-[#D06892]" />
            <p className="text-3xl font-bold mt-2">{data.streak_days || 0}</p>
            <p className="text-[10px] font-bold uppercase opacity-60 mt-1">Dagars streak</p>
          </div>
          <div className="neo-card p-4 bg-white text-center" data-testid="buddy-points">
            <Trophy className="w-7 h-7 mx-auto text-[#E0A330]" />
            <p className="text-3xl font-bold mt-2">{(data.total_points || 0).toLocaleString('sv-SE')}</p>
            <p className="text-[10px] font-bold uppercase opacity-60 mt-1">Totala poäng</p>
          </div>
        </div>

        <div className="neo-card p-4 bg-white" data-testid="buddy-week">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-bold uppercase opacity-60">Senaste 7 dagar</p>
              <p className="text-2xl font-bold mt-1">{data.tasks_completed_week} klara tasks</p>
            </div>
            <Sparkles className="w-7 h-7 text-[#CDB4DB]" />
          </div>
        </div>

        {data.pet && (
          <div className="neo-card p-4 bg-white text-center" data-testid="buddy-pet">
            <p className="text-[10px] font-bold uppercase opacity-60">Husdjur</p>
            <p className="text-2xl font-bold capitalize mt-1">{data.pet.name}</p>
            <p className="text-xs opacity-70">Nivå {data.pet.level || 1}</p>
          </div>
        )}

        <div className="neo-card p-4 bg-[#FFF4D6] text-center text-xs opacity-80">
          <Heart className="w-5 h-5 inline text-[#D06892] mb-1" />
          <p className="font-bold mt-1">Skicka en uppmuntran direkt!</p>
          <p className="opacity-70 mt-1">Stötta {data.display_name} med ett vänligt meddelande, oavsett hur dagen sett ut.</p>
        </div>

        <div className="text-center pt-4 pb-8">
          <p className="text-[10px] opacity-50">Skapad via Focus Critter — ADHD-vänlig livsplanerare</p>
        </div>
      </div>
    </div>
  );
}
